import React, { useCallback, useEffect, useRef, useState } from "react"
import Client from "../../models/client"
import useSWR from "swr"
import { createCouponContent, fetchCouponContent, updateCouponContent } from "../api"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, Loader, Radio, ScrollArea, Select, Stack, Text, TextInput } from "@mantine/core"
import CouponContentEditor from "./CouponContentEditor"
import { confirmDiscardChanges } from "../utils/confirm"

const URL_REGEX =
  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/

interface Props {
  opened: boolean
  onClose: () => void
  client: Client
  couponContentId: number | null
  onSaved?: () => void
}

interface FormValues {
  to_type: string
  title: string
  url: string
  content: string
  is_active: string
}

const defaultValues: FormValues = {
  to_type: 'inviter',
  title: '',
  url: '',
  content: '',
  is_active: 'true',
}

const CouponContentFormModal: React.FC<Props> = ({
  opened,
  onClose,
  client,
  couponContentId,
  onSaved
}) => {
  const isNew = couponContentId === null
  const [submitting, setSubmitting] = useState(false)

  const { data, isLoading } = useSWR(
    opened && !isNew
      ? `/admin/clients/${client.id}/coupon_contents/${couponContentId}`
      : null,
    () => fetchCouponContent(client.id, couponContentId as number)
  )

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      to_type: (v) => (!v ? '必須です' : null),
      title: (v) =>
        !v
          ? 'ページタイトルは必須です'
          : v.length > 255
            ? '255文字以内で入力してください'
            : null,
      url: (v) =>
        !v
          ? '公式URLは必須です'
          : !URL_REGEX.test(v)
            ? 'URLの形式が正しくありません'
            : null,
      content: (v) => {
        const text = (v ?? '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim()
        const hasMedia = /<img\b/i.test(v ?? '')
        return !text && !hasMedia ? 'ページ内容は必須です' : null
      },
    },
  })

  const formRef = useRef(form)
  formRef.current = form
  const handleContentChange = useCallback((value: string) => {
    formRef.current.setFieldValue('content', value)
    formRef.current.clearFieldError('content')
  }, [])

  useEffect(() => {
    if (!opened) return
    if (isNew) {
      form.setValues(defaultValues)
      form.resetDirty()
      return
    }
    if (data) {
      form.setValues({
        to_type: data.to_type ?? 'inviter',
        title: data.title ?? '',
        url: data.body_json?.url ?? '',
        content: data.body_json?.content ?? '',
        is_active: String(data.is_active),
      })
      form.resetDirty()
    }
  }, [opened, data, isNew])

  const handleClose = () => {
    if (
      form.isDirty() &&
      !confirmDiscardChanges()
    ) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    try {
      const logoUrl = isNew ? '' : (data?.body_json?.logo_url ?? '')
      if (isNew) {
        await createCouponContent(
          client.id,
          values.to_type,
          values.title,
          logoUrl,
          values.url,
          values.content,
          values.is_active === 'true',
        )
        notifications.show({ message: 'クーポン画面を作成しました', color: 'teal' })
      } else {
        await updateCouponContent(
          client.id,
          couponContentId as number,
          values.to_type,
          values.title,
          logoUrl,
          values.url,
          values.content,
          values.is_active === 'true',
        )
        notifications.show({ message: 'クーポン画面を更新しました', color: 'teal' })
      }
      onSaved?.()
      onClose()
    } catch (e: any) {
      console.error('クーポン画面の保存に失敗しました', e.response?.status, e.response?.data)
      const errData = e.response?.data
      const msg = errData?.errors?.join('\n') ?? errData?.error ?? '保存に失敗しました'
      notifications.show({ message: msg, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  const showLoader = !isNew && (isLoading || !data)

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title={isNew ? 'クーポン画面作成' : 'クーポン画面編集'}
      width={1000}
      size="85vh"
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      {showLoader ? (
        <Group justify="center" py="xl" style={{ width: '100%' }}>
          <Loader />
        </Group>
      ) : (
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
            <Stack gap="md" p="md">
              <Select
                label="宛先"
                data={[
                  { value: 'inviter', label: '紹介者' },
                  { value: 'guest', label: 'ゲスト' },
                ]}
                allowDeselect={false}
                {...form.getInputProps('to_type')}
              />

              <TextInput
                label={<><span>ページタイトル</span>{requiredMark}</>}
                styles={requireLabelStyles}
                {...form.getInputProps('title')}
              />

              <TextInput
                label={<><span>公式URL</span>{requiredMark}</>}
                description="クーポン受け取り後のページに表示される「公式サイトへ」ボタンのリンク先として使用します"
                styles={requireLabelStyles}
                {...form.getInputProps('url')}
              />

              <Box>
                <Text size="sm" fw={500} mb={4} style={requireLabelStyles.label}>
                  <span>ページ内容</span>
                  {requiredMark}
                </Text>
                <Text size="xs" c="var(--mantine-color-dimmed)" mb={8}>
                  ※プレビューは「来店クーポン画面」からご確認いただけます。（下部の「保存」ボタンを押すと自動的に遷移します）
                </Text>
                <Box
                  bg="#fff"
                  p="sm"
                  style={{ borderRadius: 8, border: '1px solid #0000001A' }}
                >
                  <div style={{ display: 'flex', gap: 30 }}>
                    <div>
                      <img src="/img/coupon/edit.png" />
                    </div>
                    <CouponContentEditor
                      key={isNew ? 'new' : String(couponContentId)}
                      initialValue={isNew ? '' : (data?.body_json?.content ?? '')}
                      client={client}
                      onChange={handleContentChange}
                    />
                  </div>
                </Box>
                {form.errors.content && (
                  <Text c="red" size="sm" mt={4}>{form.errors.content}</Text>
                )}
              </Box>

              <Radio.Group label="ステータス" {...form.getInputProps('is_active')}>
                <Group mt="xs">
                  <Radio value="true" label="有効" />
                  <Radio value="false" label="無効" />
                </Group>
              </Radio.Group>
            </Stack>
          </ScrollArea>

          <Box
            p="md"
            style={{
              borderTop: '1px solid #0000001A',
              backgroundColor: '#fff',
              flexShrink: 0,
            }}
          >
            <Group justify="flex-end" gap={8}>
              <Button
                variant="outline"
                radius="xl"
                color="gray"
                onClick={handleClose}
                disabled={submitting}
              >
                キャンセル
              </Button>
              <Button
                variant="filled"
                radius="xl"
                type="submit"
                loading={submitting}
              >
                保存
              </Button>
            </Group>
          </Box>
        </form>
      )}
    </BaseModal>
  )
}

export default CouponContentFormModal
